import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, downloadWithAuth } from './api';
import { getImpersonation, exitImpersonation as exitImpersonationState } from './impersonation';
import { useAuth } from './AuthContext';
import { Home, FileText, Upload, MessageSquare, LogOut, Sparkles, CheckCircle2, Circle, Send, Loader2, Phone, Mail, Download, X, AlertCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { LOAN_TYPE_LABELS, labelFor } from './shared/labels';
import { formatPhone, telHref } from './shared/phone';
import SimpleMarkdown from '../components/SimpleMarkdown';
import { formatDateTime } from './kit/time';
import MyDealConversations from './MyDealConversations';
import { Input } from '../components/ui/input';

const DOC_TYPES = [['paystub','Paystub'],['w2','W-2'],['tax_return','Tax Return'],['bank_statement','Bank Statement'],['id','ID / License'],['other','Other']];
const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPES);
let queueSeq = 0;

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

export default function BorrowerPortalPage() {
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  // Files waiting for "Send", each with the type picked when it was added (ticket 216).
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileType, setFileType] = useState('paystub');
  const [uploading, setUploading] = useState(false);
  const [messages, setMessages] = useState([{ role: 'audrey', text: 'Hi! I’m Audrey. I can answer any question about your mortgage, the process, or the documents you’re seeing. What can I help with?' }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef();
  const navigate = useNavigate();

  const load = async () => { try { const { data: d } = await api.get('/borrower/my-loan'); setData(d); } catch {} };
  useEffect(() => { if (user?.role === 'borrower') load(); }, [user]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin" style={{ color: '#6BA82E' }} /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.role !== 'borrower') return <Navigate to="/portal" replace />;

  const maxMb = data?.upload_max_mb; // the server's cap, sent with /borrower/my-loan
  const doLogout = async () => { await logout(); navigate('/portal/login'); };

  const addFiles = (files) => {
    const added = Array.from(files || []).map((f) => ({
      id: ++queueSeq,
      file: f,
      fileType,
      error: maxMb && f.size > maxMb * 1024 * 1024 ? `Larger than ${maxMb} MB` : null,
    }));
    if (added.length) setQueue((q) => [...q, ...added]);
  };
  const removeQueued = (id) => setQueue((q) => q.filter((item) => item.id !== id));
  const onDrop = (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer?.files); };

  // One request per file, in order; a refused file stays listed with the reason.
  const upload = async () => {
    const ready = queue.filter((item) => !item.error);
    if (!ready.length) return;
    setUploading(true);
    let sent = 0;
    for (const item of ready) {
      const fd = new FormData(); fd.append('file', item.file); fd.append('file_type', item.fileType);
      try {
        await api.post('/borrower/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        sent += 1;
        removeQueued(item.id);
      } catch (e) {
        const detail = e.response?.data?.detail;
        const reason = typeof detail === 'string' ? detail : 'Upload failed. Try again.';
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, error: reason } : x)));
      }
    }
    if (sent) {
      toast({ title: sent === 1 ? 'Uploaded 1 file' : `Uploaded ${sent} files`, description: 'Your loan officer has been notified' });
      load();
    }
    if (sent < ready.length) toast({ title: 'Some files were not sent', description: 'They are still listed with the reason.' });
    setUploading(false);
  };
  const readyCount = queue.filter((item) => !item.error).length;

  const send = async () => {
    const t = input.trim(); if (!t || sending) return;
    setInput(''); setMessages(m => [...m, { role: 'user', text: t }]); setSending(true);
    try { const { data: r } = await api.post('/borrower/chat', { message: t }); setMessages(m => [...m, { role: 'audrey', text: r.reply }]); }
    catch { setMessages(m => [...m, { role: 'audrey', text: 'Sorry, I hit an error. Please try again or contact your loan officer.' }]); }
    setSending(false);
  };

  const lead = data?.lead;

  // Impersonation banner — shown when the owner has assumed a borrower's identity for preview
  const imp = getImpersonation();
  const exitImpersonation = () => {
    const restored = exitImpersonationState();
    window.location.href = restored ? '/portal/super-admin' : '/portal/login';
  };

  return (
    <div className="min-h-screen" style={{ background: '#F5F6FA' }}>
      {imp && (
        <div
          className="w-full text-white text-center text-[13px] font-semibold py-1.5 flex items-center justify-center gap-3"
          style={{ background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' }}
          data-testid="impersonation-banner-borrower"
        >
          🎭 Previewing <b>{imp.tenant_name}</b> borrower portal as <b>{imp.as_email}</b>
          <button onClick={exitImpersonation} data-testid="exit-impersonation-borrower" className="ml-2 text-[11px] font-bold px-2.5 py-0.5 rounded bg-white/95 hover:bg-white" style={{ color: '#B91C1C' }}>
            EXIT PREVIEW
          </button>
        </div>
      )}      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg">
              <img src="/assets/tcl-logo.png" alt="Top Choice Lending" className="h-10 w-auto" />
            </div>
            <div className="leading-tight hidden md:block">
              <div className="text-xs uppercase tracking-widest" style={{ color: '#6BA82E' }}>Borrower Portal</div>
              <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Welcome, {user.name?.split(' ')[0]}</div>
            </div>
          </div>
          <button onClick={doLogout} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      {!lead ? (
        <div className="max-w-2xl mx-auto p-10 text-center">
          <div className="text-left"><MyDealConversations /></div>
          <Home size={40} className="mx-auto text-slate-300 mb-3" />
          <h2 className="font-serif text-2xl font-bold" style={{ color: '#070B5F' }}>No loan linked yet</h2>
          <p className="text-slate-500 mt-2">{data?.message || 'Your loan officer will link your file to your account soon.'}</p>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          <MyDealConversations />
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest" style={{ color: '#6BA82E' }}>Your Loan</div>
                <h1 className="font-serif text-3xl font-bold mt-1" style={{ color: '#070B5F' }}>{lead.property_address || 'Home Loan'}</h1>
                <div className="text-slate-500 mt-1">{labelFor(LOAN_TYPE_LABELS, lead.loan_type)} • {fmt$(lead.loan_amount)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-500">Current Status</div>
                <div className="font-serif text-xl font-bold mt-1" style={{ color: '#6BA82E' }}>{data.loan_status?.label || 'In progress'}</div>
              </div>
            </div>
          </div>

          {data.lo_contact && (
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-6" data-testid="lo-contact">
              <div className="text-xs uppercase tracking-widest text-slate-500">Your Loan Officer</div>
              <div className="font-serif text-lg font-bold mt-1" style={{ color: '#070B5F' }}>{data.lo_contact.name}</div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                {data.lo_contact.phone && <a href={telHref(data.lo_contact.phone)} className="flex items-center gap-1.5 hover:underline"><Phone size={13} /> {formatPhone(data.lo_contact.phone)}</a>}
                {data.lo_contact.email && <a href={`mailto:${data.lo_contact.email}`} className="flex items-center gap-1.5 hover:underline"><Mail size={13} /> {data.lo_contact.email}</a>}
                {data.lo_contact.nmls && <span>NMLS #{data.lo_contact.nmls}</span>}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-4 bg-white rounded-xl p-1 w-fit shadow-sm border border-slate-100">
            {[['overview', 'Overview', Home], ['docs', 'My Documents', FileText], ['upload', 'Upload', Upload], ['chat', 'Ask Audrey', MessageSquare]].map(([k, l, Icon]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === k ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} style={tab === k ? { background: '#070B5F' } : {}}>
                <Icon size={14} /> {l}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Progress</div>
                {data.loan_status?.closed && <div className="text-sm text-slate-600 mb-4" data-testid="loan-closed-note">This file is closed. Please contact your loan officer with any questions.</div>}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="progress-steps">
                  {(data.loan_status?.steps || []).map((st) => (
                    <div key={st.key} className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: st.reached ? '#6BA82E' : '#e2e8f0', color: st.reached ? 'white' : '#94a3b8' }}>{st.reached ? <CheckCircle2 size={18} /> : <Circle size={18} />}</div>
                      <div className="text-xs font-semibold text-center" style={{ color: st.reached ? '#070B5F' : '#94a3b8' }}>{st.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="font-serif font-semibold mb-3" style={{ color: '#070B5F' }}>Recent Activity</div>
                {data.activities.length === 0 ? <div className="text-slate-400 text-sm">No activity yet.</div> :
                  <div className="space-y-2">{data.activities.slice(0, 8).map(a => (
                    <div key={a.activity_id} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-b-0">
                      <Sparkles size={13} className="mt-1" style={{ color: '#6BA82E' }} />
                      <div className="flex-1"><div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{a.title}</div><div className="text-[11px] text-slate-400 mt-0.5">{formatDateTime(a.created_at)}</div></div>
                    </div>
                  ))}</div>
                }
              </div>
            </div>
          )}

          {tab === 'docs' && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Your Documents</div>
              {data.documents.length === 0 ? <div className="text-slate-400 text-sm py-8 text-center">No documents yet.</div> :
                <div className="space-y-2">{data.documents.map(d => (
                  <div key={d.document_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <FileText size={16} style={{ color: '#6BA82E' }} />
                    <div className="flex-1"><div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{d.filename}</div><div className="text-xs text-slate-500 uppercase">{d.file_type.replace('_',' ')} • {(d.size_bytes/1024).toFixed(1)} KB</div></div>
                    <button type="button" onClick={() => downloadWithAuth(`/borrower/documents/${d.document_id}/download`, d.filename)} aria-label={`Download ${d.filename}`} className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-slate-900"><Download size={16} /></button>
                  </div>
                ))}</div>
              }
            </div>
          )}

          {tab === 'upload' && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Upload a Document</div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold" style={{ color: '#070B5F' }}>Document Type</label>
                  <Select value={fileType} onValueChange={setFileType}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label
                  data-testid="upload-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  className={`block border-2 border-dashed rounded-xl p-8 text-center hover:border-[#6BA82E] cursor-pointer ${dragging ? 'border-[#6BA82E] bg-[#6BA82E]/5' : 'border-slate-200'}`}
                >
                  <input type="file" hidden multiple data-testid="upload-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                  <Upload size={24} className="mx-auto text-slate-400 mb-2" />
                  <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>Click to choose files, or drop them here</div>
                  <div className="text-xs text-slate-500 mt-1">{maxMb ? `Up to ${maxMb} MB each — encrypted upload` : 'Encrypted upload'}</div>
                </label>
                {queue.length > 0 && (
                  <div className="space-y-2">{queue.map((item) => (
                    <div key={item.id} data-testid="upload-queue-row" className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                      <FileText size={16} style={{ color: '#6BA82E' }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>{item.file.name}</div>
                        <div className="text-xs text-slate-500 uppercase">{DOC_TYPE_LABEL[item.fileType] || item.fileType} • {(item.file.size / 1024).toFixed(1)} KB</div>
                        {item.error && <div className="text-xs text-red-700 mt-0.5">{item.error}</div>}
                      </div>
                      {item.error
                        ? <AlertCircle size={18} className="text-red-600 shrink-0" aria-label="Not sent" />
                        : <CheckCircle2 size={18} className="shrink-0" style={{ color: '#6BA82E' }} aria-label="Ready to send" />}
                      <button type="button" onClick={() => removeQueued(item.id)} disabled={uploading} aria-label={`Remove ${item.file.name}`} className="p-1.5 rounded-lg text-slate-400 hover:bg-white hover:text-slate-900 disabled:opacity-50"><X size={14} /></button>
                    </div>
                  ))}</div>
                )}
                <button onClick={upload} disabled={!readyCount || uploading} className="btn-gold w-full justify-center">
                  {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                    : readyCount > 1 ? `Send ${readyCount} files to Loan Officer` : 'Send to Loan Officer'}
                </button>
              </div>
            </div>
          )}

          {tab === 'chat' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col h-[600px]">
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A6E56C, #6BA82E)' }}><Sparkles size={18} style={{ color: '#070B5F' }} /></div>
                <div><div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Audrey</div><div className="text-xs text-slate-500">Your friendly mortgage guide</div></div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : ''}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'text-white' : 'bg-slate-100'}`} style={m.role === 'user' ? { background: '#070B5F' } : { color: '#0f172a' }}>{m.role === 'user' ? m.text : <SimpleMarkdown text={m.text} />}</div>
                  </div>
                ))}
                {sending && <div className="flex"><div className="bg-slate-100 rounded-2xl px-4 py-3 flex gap-1">{[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-slate-400" style={{ animation: 'audreyPulse 1s ease-in-out infinite', animationDelay: `${i*150}ms` }} />)}</div></div>}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask a question..." className="flex-1 w-auto h-auto px-4 py-2.5 rounded-full md:text-base" />
                <button type="submit" disabled={sending || !input.trim()} className="btn-gold"><Send size={14} /></button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
